import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  ActivityActor,
  ActivityEventInput,
  ActivityPage,
  ActivityQuery,
  ActivitySummary,
} from '@internal/plugin-activity-common';

export interface ActivityApi {
  list(query: ActivityQuery): Promise<ActivityPage>;
  summary(query: ActivityQuery): Promise<ActivitySummary>;
  actors(query: ActivityQuery): Promise<ActivityActor[]>;
  mine(query: ActivityQuery): Promise<ActivityPage>;
  /** Fire-and-forget. Never throws: recording must not break the page. */
  record(events: ActivityEventInput[]): Promise<void>;
}

export const activityApiRef = createApiRef<ActivityApi>({
  id: 'plugin.activity.service',
});

/** Turns a query object into the flat query string the router reads. */
function toParams(query: ActivityQuery): string {
  const params = new URLSearchParams();
  if (query.actorRef) params.set('actor', query.actorRef);
  if (query.actorKind) params.set('actorKind', query.actorKind);
  if (query.type?.length) params.set('type', query.type.join(','));
  if (query.entityRef) params.set('entityRef', query.entityRef);
  if (query.pluginId) params.set('pluginId', query.pluginId);
  if (query.outcome) params.set('outcome', query.outcome);
  if (query.source) params.set('source', query.source);
  if (query.q) params.set('q', query.q);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export class ActivityClient implements ActivityApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async request<T>(path: string): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('activity');
    const res = await this.fetchApi.fetch(`${base}${path}`);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  list(query: ActivityQuery) {
    return this.request<ActivityPage>(`/events${toParams(query)}`);
  }

  summary(query: ActivityQuery) {
    return this.request<ActivitySummary>(`/summary${toParams(query)}`);
  }

  async actors(query: ActivityQuery) {
    const { actors } = await this.request<{ actors: ActivityActor[] }>(
      `/actors${toParams(query)}`,
    );
    return actors;
  }

  mine(query: ActivityQuery) {
    return this.request<ActivityPage>(`/me${toParams(query)}`);
  }

  async record(events: ActivityEventInput[]): Promise<void> {
    if (events.length === 0) return;
    try {
      const base = await this.discoveryApi.getBaseUrl('activity');
      await this.fetchApi.fetch(`${base}/events`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ events }),
        // Lets the browser finish the request after the tab goes away, which
        // is exactly when the last batch of a session is sent.
        keepalive: true,
      });
    } catch {
      // Swallowed on purpose. A trail that can break the page it is
      // recording would not survive its first outage.
    }
  }
}
